import React from "react";
import { useLocation } from "react-router-dom";
import {
    Card,
    CardContent,
    Typography,
    Stack,
    Chip,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";


type IngestsData = {
    best_topic: string;
    duration_sec: number;
    entities: {
        dates: string[];
        names: string[];
        organizations: string[];
    };
    filename: string;
    important_words: string[];
    language: string;
    priority: string;
    sentiment: string;
    sentiment_confidence: number;
    severity_score: number;
    size_mb: number;
    response_time: number;
    summary: string;
    topics: string[];
    call_category : {
        call_type : string;
        complaint_type : string
    };
    transcript: string;
};

const Ingests: React.FC = () => {
    const location = useLocation();
    const data = location.state as IngestsData;

    if (!data) return <p>No data provided</p>;
    return (
        <Card sx={{ maxWidth: 600, mx: "auto", mt: 4, p: 2, boxShadow: 3 }}>
            <CardContent>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Call Details
                </Typography>

                {/* Call Category & Type */}
                <Stack direction="row" justifyContent="space-between" spacing={2} my={1}>
                    <Typography variant="subtitle1">
                        <strong>Type:</strong> <span>{data.call_category.call_type}</span>
                    </Typography>
                </Stack>

                {/* Conditional Complaint Type */}
                {data.call_category.call_type === 'Complaint' && (
                    <Typography variant="subtitle1" sx={{ my: 1 }}>
                        <strong>Complaint Type:</strong> <span>{data.call_category.complaint_type}</span>
                    </Typography>
                )}

                {/* Priority & Severity */}
                <Stack direction="row" justifyContent="space-between" spacing={2} my={1}>
                    <Typography variant="subtitle1">
                        <strong>Priority:</strong> <span style={{ color: "#1E88E5" }}>{data.priority}</span>
                    </Typography>
                    <Typography variant="subtitle1">
                        <strong>Severity Score:</strong> <span style={{ color: "#D32F2F" }}>{data.severity_score}</span>
                    </Typography>
                </Stack>

                {/* Summary */}
                <Typography variant="body1" fontStyle="italic" color="text.secondary" my={2}>
                    {data.summary}
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Entities */}
                <Typography variant="h6" fontWeight="bold">Entities:</Typography>
                <Typography variant="body1"><strong>Names:</strong> {data.entities.names.join(", ")}</Typography>
                <Typography variant="body1"><strong>Organizations:</strong> {data.entities.organizations.join(", ")}</Typography>

                <Divider sx={{ my: 2 }} />

                {/* Important Words */}
                <Typography variant="h6" fontWeight="bold">Important Words:</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} my={1}>
                    {data.important_words.map((word, index) => (
                        <Chip key={index} label={word} color="primary" variant="outlined" />
                    ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* Sentiment */}
                <Typography variant="h6" fontWeight="bold">Sentiment:</Typography>
                <Typography variant="body1" color="success.main">
                    {data.sentiment} (Confidence: {data.sentiment_confidence})
                </Typography>

                <Divider sx={{ my: 2 }} />

                {/* Audio File Info */}
                <Typography variant="h6" fontWeight="bold">Audio File:</Typography>
                <Typography variant="body1"><strong>Filename:</strong> {data.filename}</Typography>
                <Typography variant="body1"><strong>Duration:</strong> {data.duration_sec} sec</Typography>
                <Typography variant="body1"><strong>Size:</strong> {data.size_mb} MB</Typography>
                <Typography variant="body1"><strong>Response Time :</strong> {data.response_time} sec</Typography>

                <Divider sx={{ my: 2 }} />

                {/* Transcript - Expandable */}
                <Accordion sx={{ mt: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" fontWeight="bold">Full Transcript</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body1" color="text.secondary">
                            {data.transcript}
                        </Typography>
                    </AccordionDetails>
                </Accordion>

            </CardContent>
        </Card>
    );
};
export default Ingests;